'use client';

import { useRef, useState, useEffect } from 'react';
import { motion, useSpring, useTransform, useMotionValue } from 'framer-motion';
import { ArrowRight, Calendar, TrendingUp, Clock, ShieldCheck, Mail } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* ── Looping 3D cube flip between two headline lines ── */
/*
 * True 4-face CSS cube:
 * - Outer: perspective + clipPath:inset(0) for clipping WITHOUT flattening 3D
 * - Inner motion.div: transformStyle:preserve-3d, animates rotateX in steps of -90deg (upward)
 * - 4 face spans positioned at rotateX(0|90|180|270deg) translateZ(half-height)
 *   We repeat the two texts so faces 0/2 = text A and faces 1/3 = text B (seamless loop)
 */
const CUBE_FACES = [
    { text: 'Analizamos tu negocio.', gradient: false },
    { text: 'Y lo mejoramos.', gradient: true },
    { text: 'Analizamos tu negocio.', gradient: false },
    { text: 'Y lo mejoramos.', gradient: true },
];

function CyclingCube() {
    const [step, setStep] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => setStep(s => s + 1), 2800);
        return () => clearInterval(timer);
    }, []);

    // em-based half-height: font-size * line-height / 2 ≈ 0.55em
    const tz = '0.55em';

    return (
        <div style={{ perspective: '400px', height: '1.2em', clipPath: 'inset(-0.5em -0.5em)', paddingLeft: '0.05em' }}>
            <motion.div
                animate={{ rotateX: -step * 90 }}
                transition={{ duration: 0.55, ease: EASE_OUT }}
                style={{ transformStyle: 'preserve-3d', position: 'relative', height: '100%' }}
            >
                {CUBE_FACES.map((face, i) => (
                    <span
                        key={i}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            transform: `rotateX(${i * 90}deg) translateZ(${tz})`,
                            backfaceVisibility: 'hidden',
                            display: 'block',
                            lineHeight: 1,
                        }}
                        className={face.gradient
                            ? 'text-blue-600'
                            : 'text-slate-900'}
                    >
                        {face.text}
                    </span>
                ))}
            </motion.div>
        </div>
    );
}

/* ── Counter that animates from 0 ── */
interface CounterProps {
    value: string | number;
    duration?: number;
}

function Counter({ value, duration = 1.2 }: CounterProps) {
    const [display, setDisplay] = useState(0);
    const started = useRef<boolean>(false);
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        const end = parseFloat(String(value));
        const step = end / (duration * 60);
        let cur = 0;
        const timer = setInterval(() => {
            cur += step;
            if (cur >= end) { clearInterval(timer); setDisplay(end); }
            else setDisplay(Math.floor(cur));
        }, 1000 / 60);
        return () => clearInterval(timer);
    }, [value, duration]);
    return display;
}

/* ── Floating email notification card — top-right ── */
function EmailNotificationCard() {
    return (
        <motion.div
            initial={{ opacity: 0, x: 10, y: -10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7, ease: EASE_OUT }}
            className="absolute -top-6 right-0 glass-card rounded-2xl px-3.5 py-3 flex items-start gap-2.5 shadow-lg max-w-[210px] hidden sm:flex"
        >
            <div className="w-7 h-7 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                <Mail className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <div>
                <p className="text-[10px] font-semibold text-slate-800 leading-tight">Diagnóstico completado</p>
                <p className="text-[9px] text-slate-500 leading-tight mt-0.5">Tu sector · Madrid</p>
                <p className="text-[9px] text-indigo-500 font-medium mt-1">→ 4 mejoras identificadas</p>
            </div>
        </motion.div>
    );
}

/* ── Dashboard 3D CSS mockup ── */
function DashboardMockup() {
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [4, -4]), { stiffness: 150, damping: 20 });
    const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 150, damping: 20 });

    // Throttle mousemove to reduce INP — only update every ~32ms (≈30fps)
    const lastMoveRef = useRef(0);
    const handleMouseMove = (e: any) => {
        const now = performance.now();
        if (now - lastMoveRef.current < 32) return;
        lastMoveRef.current = now;
        const rect = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleMouseLeave = () => { x.set(0); y.set(0); };

    return (
        <div
            className="relative w-full max-w-[520px] mx-auto lg:mx-0"
            style={{ perspective: '1200px' }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            {/* Glows */}
            <div className="absolute inset-0 -z-10 m-auto w-[80%] h-[70%] bg-indigo-300/20 rounded-full blur-[70px]" />
            <div className="absolute inset-0 -z-10 m-auto w-[50%] h-[50%] bg-sky-300/15 rounded-full blur-[40px]" />

            <motion.div
                style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
                initial={{ opacity: 0, y: 30, rotateX: 8, rotateY: -10 }}
                animate={{ opacity: 1, y: 0, rotateX: 4, rotateY: -8 }}
                transition={{ duration: 1.2, ease: EASE_OUT, delay: 0.15 }}
                className="rounded-2xl overflow-hidden shadow-[0_32px_80px_rgba(99,102,241,0.18),0_8px_24px_rgba(15,23,42,0.1)]"
            >
                {/* Browser chrome */}
                <div className="flex items-center gap-1.5 px-4 py-3 bg-white/90 border-b border-slate-200/60 backdrop-blur-xl">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                    <div className="ml-3 flex-1 h-5 rounded-md bg-slate-100 border border-slate-200/60 flex items-center px-2 gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                        <span className="text-[9px] text-slate-400 font-mono">innovatheratech.com</span>
                    </div>
                </div>

                {/* Dashboard */}
                <div className="bg-white/88 backdrop-blur-xl p-5 space-y-3">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-slate-700">Diagnóstico en curso</span>
                        <span className="text-[9px] font-mono text-green-600 bg-green-50 border border-green-200/60 px-2 py-0.5 rounded-full">● En vivo</span>
                    </div>

                    {/* Metric cards */}
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { bg: 'bg-blue-50', border: 'border-blue-100', val: '6', text: 'blue', label: 'Problemas detectados' },
                            { bg: 'bg-indigo-50', border: 'border-indigo-100', val: '15h', text: 'indigo', label: 'Ahorro/semana' },
                            { bg: 'bg-violet-50', border: 'border-violet-100', val: '3', text: 'violet', label: 'Soluciones propuestas' },
                        ].map((m, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 + i * 0.1, duration: 0.5 }}
                                className={`${m.bg} border ${m.border} rounded-xl p-3 text-center`}
                            >
                                <div className={`text-base font-bold text-${m.text}-700 leading-none`}>{m.val}</div>
                                <div className={`text-[9px] text-${m.text}-500/80 mt-1 font-medium`}>{m.label}</div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Chart */}
                    <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[9px] font-semibold text-slate-600">Eficiencia operativa</span>
                            <span className="text-[9px] text-green-600 font-medium">+34%</span>
                        </div>
                        <div className="flex items-end gap-1 h-10">
                            {[40, 55, 48, 65, 58, 72, 88].map((h, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ scaleY: 0 }}
                                    animate={{ scaleY: 1 }}
                                    transition={{ delay: 0.7 + i * 0.06, duration: 0.45, ease: 'easeOut' as const }}
                                    style={{ height: `${h}%`, transformOrigin: 'bottom', flex: 1 }}
                                    className={`rounded-sm ${i === 6 ? 'bg-blue-500' : 'bg-blue-200/70'}`}
                                />
                            ))}
                        </div>
                        <div className="flex justify-between mt-1">
                            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
                                <span key={d} className="text-[8px] text-slate-400 flex-1 text-center">{d}</span>
                            ))}
                        </div>
                    </div>

                    {/* Status row */}
                    <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-1.5 bg-green-50/80 border border-green-100 rounded-xl px-3 py-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[9px] text-green-700 font-medium">Análisis completado</span>
                        </div>
                        <div className="flex-1 flex items-center gap-1.5 bg-sky-50/80 border border-sky-100 rounded-xl px-3 py-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                            <span className="text-[9px] text-sky-700 font-medium">Propuesta lista</span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Floating email notification — top right */}
            <EmailNotificationCard />

            {/* Bottom-left badge */}
            <motion.div
                initial={{ opacity: 0, x: -10, y: 10 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ delay: 1.6, duration: 0.6, ease: EASE_OUT }}
                className="absolute -bottom-5 left-0 glass-card rounded-2xl px-3 py-2 flex items-center gap-2"
            >
                <ShieldCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span className="text-[10px] font-semibold text-slate-700">100% tuyo</span>
            </motion.div>
        </div>
    );
}

/* ── Stat cell — editorial bar segment ── */
function StatPill({ icon: Icon, value, label, delay = 0 }: { icon: any, value: string, label: string, delay?: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay, duration: 0.5, ease: EASE_OUT }}
            className="flex flex-col gap-1 flex-1 px-3 sm:px-5 py-3 sm:py-3.5 sm:min-w-[130px]"
        >
            <div className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span className="text-base font-bold text-slate-900 tracking-tight leading-none">{value}</span>
            </div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-medium">{label}</p>
        </motion.div>
    );
}

export default function Hero() {
    return (
        <section className="relative flex min-h-screen items-center overflow-x-hidden pt-24 pb-20">
            <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
                <div className="grid grid-cols-1 items-center gap-12 sm:gap-16 lg:grid-cols-2">

                    {/* ── Left: Copy ── */}
                    <div className="order-1 min-w-0">
                        {/* Badge — shadcn dot variant */}
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.05, ease: 'easeOut' as const }}
                            className="mb-7"
                        >
                            <Badge variant="dot" className="text-[11px] tracking-[0.04em]">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-600" />
                                </span>
                                Consultora · Implementadora · España
                            </Badge>
                        </motion.div>

                        {/* Headline — looping cube flip */}
                        <h1 className="text-[1.5rem] font-black tracking-tight sm:text-4xl md:text-5xl lg:text-[3.25rem]">
                            <CyclingCube />
                        </h1>

                        {/* Editorial accent line — adds personality */}
                        <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[10px] sm:text-[11px] font-mono tracking-[0.14em] sm:tracking-[0.22em] uppercase text-slate-400">
                            <span>Diagnosticamos</span>
                            <span className="hidden sm:block h-px w-6 bg-slate-300 shrink-0" />
                            <span>Diseñamos</span>
                            <span className="hidden sm:block h-px w-6 bg-slate-300 shrink-0" />
                            <span>Implementamos</span>
                        </div>

                        {/* Sub — visible immediately (LCP element), only animate transform */}
                        <p className="mt-6 max-w-md text-base sm:text-lg font-light leading-relaxed text-slate-600">
                            Analizamos cómo trabaja tu negocio por dentro, detectamos lo que no funciona y lo arreglamos nosotros mismos.
                            Sin intermediarios. Sin consultores que se van después del PDF.
                        </p>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.5, ease: EASE_OUT }}
                            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
                        >
                            <Button asChild size="lg" variant="default">
                                <a href="#contacto">
                                    <Calendar />
                                    Quiero un presupuesto
                                </a>
                            </Button>
                            <Button asChild size="lg" variant="secondary">
                                <a href="#nicho-selector">
                                    ¿Cuál es tu sector? <span aria-hidden>↓</span>
                                </a>
                            </Button>
                        </motion.div>

                        {/* Stats — editorial bar with subtle dividers */}
                        <div className="mt-8 sm:mt-10 flex w-full sm:w-auto sm:inline-flex items-stretch rounded-2xl border border-slate-200/60 bg-white/55 backdrop-blur-sm divide-x divide-slate-200/60 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
                            <StatPill icon={TrendingUp} value="A medida" label="tecnología propia" delay={0.9} />
                            <StatPill icon={Clock} value="< 48h" label="propuesta" delay={1.0} />
                            <StatPill icon={ShieldCheck} value="100%" label="trato directo" delay={1.1} />
                        </div>
                    </div>

                    {/* ── Right: Dashboard ── */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE_OUT }}
                        className="order-2 flex items-center justify-center lg:justify-end"
                    >
                        <DashboardMockup />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
